import { Fragment } from "react";
import { cn } from "@/lib/utils";

export function LinkifiedText({
  text,
  className,
}: {
  text: string;
  className?: string;
}) {
  // Some legacy imports may store "\n" literally.
  const normalized = text.replace(/\\n/g, "\n");
  const lines = normalized.split(/\r?\n/g);

  const renderTextWithUrls = (value: string, keyPrefix: string) => {
    const urlRegex = /(https?:\/\/[^\s<>()]+|www\.[^\s<>()]+)/g;
    const nodes: Array<string | JSX.Element> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = urlRegex.exec(value))) {
      const [rawUrl] = match;
      const start = match.index;
      if (start > lastIndex) nodes.push(value.slice(lastIndex, start));
      const href = rawUrl.startsWith("http") ? rawUrl : `https://${rawUrl}`;
      nodes.push(
        <a
          key={`${keyPrefix}-url-${start}-${rawUrl}`}
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          className="text-primary no-underline hover:underline decoration-primary/35 underline-offset-2"
        >
          {rawUrl}
        </a>,
      );
      lastIndex = start + rawUrl.length;
    }
    if (lastIndex < value.length) nodes.push(value.slice(lastIndex));
    return nodes;
  };

  const renderLine = (line: string, lineIdx: number) => {
    // Markdown-style links: [label](https://url)
    const mdLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;
    const nodes: Array<string | JSX.Element> = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = mdLinkRegex.exec(line))) {
      const [full, label, href] = match;
      const start = match.index;
      if (start > lastIndex) {
        nodes.push(...renderTextWithUrls(line.slice(lastIndex, start), `l${lineIdx}-${lastIndex}`));
      }
      nodes.push(
        <a
          key={`l${lineIdx}-md-${start}-${href}`}
          href={href}
          target="_blank"
          rel="noreferrer noopener"
          className="text-primary no-underline hover:underline decoration-primary/35 underline-offset-2"
        >
          {label}
        </a>,
      );
      lastIndex = start + full.length;
    }

    if (lastIndex < line.length) {
      nodes.push(...renderTextWithUrls(line.slice(lastIndex), `l${lineIdx}-${lastIndex}`));
    }
    return nodes;
  };

  return (
    <p className={cn("whitespace-pre-line break-words", className)}>
      {lines.map((line, idx) => (
        <Fragment key={idx}>
          {renderLine(line, idx)}
          {idx < lines.length - 1 ? <br /> : null}
        </Fragment>
      ))}
    </p>
  );
}

